import { useCallback,useEffect, useState } from "react";
import api from "../api/axios";
import {
  Box,
  Button,
  TextField,
  IconButton,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import FormModal from "../components/FormModal";
import Can from "../auth/Can";
import { useNotify } from "../context/NotificationContext";
export default function Services() {
  const { notify } = useNotify();
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/services");
      setServices(r.data);
    } catch (e) {
      setError("Failed to load services");
      notify('Failed to load services','error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (service = null) => {
    setCurrent(service);
    setName(service ? service.name : "");
    setOpen(true);
  };

  const save = async () => {
    try {
      setSaving(true);
      if (current) {
        await api.put(`/services/${current.id}`, { name });
        notify('Service added successfully');
      } else {
        await api.post("/services", {
          name,
          slug: name
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-"),
        });
        notify('Service Updated Successfully')
      }
      setOpen(false);
      load();
    } catch (err) {
      console.error(err);
      notify('Something Went Wrong','error');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (window.confirm("Delete service?")) {
      await api.delete(`/services/${id}`);
      load();
      notify('Service deleted successfully')
    }
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Services
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage your service offerings
          </Typography>
        </Box>

        <Can role="admin">
          <Button
            variant="contained"
            sx={{
              px: 3,
              borderRadius: 2,
              background: "linear-gradient(135deg, #1976d2, #42a5f5)",
            }}
            onClick={() => openModal()}
            disabled
          >
            Add Service
          </Button>
        </Can>
      </Box>

      {/* Error */}
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {/* Table Card */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead
              sx={{
                background: "#111827",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <TableRow>
                <TableCell sx={{ fontWeight: "bold", color: "#ffffff" }}>
                  Service Name
                </TableCell>
                <TableCell
                  width="160"
                  sx={{ fontWeight: "bold", color: "#ffffff" }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {services.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2}>
                    <Typography
                      textAlign="center"
                      color="text.secondary"
                      sx={{ py: 4 }}
                    >
                      No services found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                services.map((s) => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{
                      "&:hover": {
                        background: "#fafafa",
                      },
                    }}
                  >
                    <TableCell>{s.name}</TableCell>
                    <TableCell>
                      <IconButton color="primary" onClick={() => openModal(s)} disabled>
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => remove(s.id)}
                        disabled
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Modal */}
      <FormModal
        open={open}
        title={current ? "Edit Service" : "Add Service"}
        onClose={() => setOpen(false)}
        onSubmit={save}
        submitting={saving}
      >
        <TextField
          fullWidth
          label="Service Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
            },
          }}
        />
      </FormModal>
    </Box>
  );
}
